import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import { handleNavigateToBorrower, handleNavigateToEntity } from 'app/services/loanNavigation';
import PopTipCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopTipCDS';
import PropTypes from 'prop-types';

import { getLabelColors, resolveColor } from 'constants/colors';
import { IconSize } from 'constants/coreDesignSystem';
import { getColorStyles } from 'utils/colorStyles';

import {
  selectEntityReferenceCandidatesByType,
  selectEntityReferenceLoadedApplicationId,
} from 'store/loanApplications/selectors';

import {
  ENTITY_REFERENCE_COLOR_NAME_BY_TYPE,
  ENTITY_REFERENCE_ICON_BY_TYPE,
  ENTITY_REFERENCE_TYPE,
} from '../TeamChat.entityReferences.utils';
import { getApplicationIdFromChannelId } from '../TeamChat.utils';

import {
  baseChipStyles,
  deltaBadgeStyles,
  hoverContentStyles,
  hoverFieldRowStyles,
  hoverHintStyles,
  inertChipStyles,
  labelStyles,
  tombstoneChipStyles,
} from './TeamChatEntityChip.styles';
import { TeamChatEntityChipTestIds } from './TeamChatEntityChip.testIds';
import {
  EntityReferenceChipState,
  getEntityChipAriaLabel,
  getEntityChipState,
  getEntityHoverFields,
} from './TeamChatEntityChip.utils';

const TeamChatEntityChip = memo(({ chatId, entityType, entityId, label }) => {
  const { palette } = useTheme();
  const history = useHistory();

  // Chips resolve on both loan channels (internal + TPO, DEV-5697); non-loan channels stay INERT.
  const applicationId = getApplicationIdFromChannelId(chatId);
  const candidatesByType = useSelector(selectEntityReferenceCandidatesByType);
  const loadedApplicationId = useSelector(selectEntityReferenceLoadedApplicationId);

  const isLoanDataLoaded =
    !!applicationId && applicationId.toLowerCase() === loadedApplicationId?.toLowerCase();
  const currentCandidate = isLoanDataLoaded
    ? (candidatesByType[entityType]?.find(candidate => candidate.entityId === entityId) ?? null)
    : null;

  const state = getEntityChipState({ applicationId, isLoanDataLoaded, currentCandidate, label });
  const isInteractive = state !== EntityReferenceChipState.INERT;
  // Tombstone keeps its hover card (so `disabled` can't be used — MUI suppresses hover events on
  // disabled elements) but must never navigate: the id no longer exists on the loan and the
  // borrower/entity routes render broken forms for stale GUIDs.
  const isNavigable =
    state === EntityReferenceChipState.NORMAL || state === EntityReferenceChipState.DELTA;

  const colorName = ENTITY_REFERENCE_COLOR_NAME_BY_TYPE[entityType];
  const Icon = ENTITY_REFERENCE_ICON_BY_TYPE[entityType];

  const resolvedColor = useMemo(() => resolveColor(colorName), [colorName]);
  const colorStyles = useMemo(
    () => getColorStyles({ resolvedColor, variant: 'contained' }),
    [resolvedColor],
  );

  const isColored =
    state === EntityReferenceChipState.NORMAL || state === EntityReferenceChipState.DELTA;
  const chipStyles = useMemo(() => {
    if (state === EntityReferenceChipState.INERT) {
      return inertChipStyles;
    }

    if (state === EntityReferenceChipState.TOMBSTONE) {
      return tombstoneChipStyles;
    }

    return { ...baseChipStyles, ...colorStyles };
  }, [state, colorStyles]);

  const iconColor = isColored
    ? getLabelColors(colorName, palette.mode === 'dark')?.color
    : undefined;
  const iconStyle = useMemo(() => ({ color: iconColor }), [iconColor]);

  const hoverFields = useMemo(
    () => getEntityHoverFields(entityType, currentCandidate?.details),
    [entityType, currentCandidate],
  );

  const onClick = useCallback(() => {
    if (!isNavigable) {
      return;
    }

    if (entityType === ENTITY_REFERENCE_TYPE.BORROWER) {
      handleNavigateToBorrower({
        parameters: { borrowerId: entityId },
        context: { history, applicationId },
      });

      return;
    }

    handleNavigateToEntity({
      parameters: { entityType, entityId },
      context: { history, applicationId },
    });
  }, [isNavigable, entityType, entityId, history, applicationId]);

  const onHintKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const ariaLabel = getEntityChipAriaLabel({ entityType, label, state });

  const chip = (
    <ButtonBase
      disableRipple
      aria-disabled={!isNavigable}
      aria-label={ariaLabel}
      component="span"
      data-testid={TeamChatEntityChipTestIds.CHIP}
      disabled={!isInteractive}
      focusRipple={false}
      sx={chipStyles}
      onClick={onClick}
    >
      <Icon size={IconSize[14]} style={iconStyle} />
      <Typography component="span" sx={labelStyles} variant="regular14">
        {label}
      </Typography>
      {state === EntityReferenceChipState.TOMBSTONE && (
        <Typography component="span" sx={labelStyles} variant="regular12">
          (removed)
        </Typography>
      )}
      {state === EntityReferenceChipState.DELTA && (
        <Box aria-hidden="true" component="span" sx={deltaBadgeStyles}>
          Δ
        </Box>
      )}
    </ButtonBase>
  );

  if (state === EntityReferenceChipState.INERT) {
    return chip;
  }

  const hoverTitle = (
    <Box sx={hoverContentStyles}>
      <Typography variant="bold14">{label}</Typography>
      {state === EntityReferenceChipState.DELTA && (
        <Typography color="text.secondary" variant="regular12">
          {`${label} → ${currentCandidate.label}`}
        </Typography>
      )}
      {state === EntityReferenceChipState.TOMBSTONE && (
        <Typography color="text.secondary" variant="regular12">
          This item was removed from the loan.
        </Typography>
      )}
      {hoverFields.map(field => (
        <Box key={field.label} sx={hoverFieldRowStyles}>
          <Typography color="text.secondary" variant="regular12">
            {field.label}
          </Typography>
          <Typography variant="regular12">{field.value}</Typography>
        </Box>
      ))}
      {state !== EntityReferenceChipState.TOMBSTONE && (
        <Typography
          color="primary.main"
          data-testid={TeamChatEntityChipTestIds.HOVER_HINT}
          role="button"
          sx={hoverHintStyles}
          tabIndex={0}
          variant="medium12"
          onClick={onClick}
          onKeyDown={onHintKeyDown}
        >
          Click to go to item
        </Typography>
      )}
    </Box>
  );

  return (
    <PopTipCDS placement="top" title={hoverTitle}>
      {chip}
    </PopTipCDS>
  );
});
TeamChatEntityChip.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(Object.values(ENTITY_REFERENCE_TYPE)).isRequired,
  label: PropTypes.string.isRequired,
  chatId: PropTypes.string,
};

export default TeamChatEntityChip;
